import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { useProjectEquipment, useProjectEquipmentConflicts } from "@/hooks/use-equipment-bookings";
import { formatDayRange } from "@/lib/equipment/availability";
import { TONE_TEXT } from "@/lib/widget-status";
import { BookingDialog } from "./BookingDialog";

const th = "py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider text-left";

/** Prosjektfanen «Utstyr» (mockup E): hvilket utstyr som er booket og når, med ⚠ og pakkeliste. */
export function ProjectEquipmentTab({ projectId }: { projectId: string }) {
  const { data: lines = [], isLoading, error } = useProjectEquipment(projectId);
  const lineIds = useMemo(() => lines.map((l) => l.id), [lines]);
  const { data: conflicts = [] } = useProjectEquipmentConflicts(projectId, lineIds);
  const [dialog, setDialog] = useState<{ bookingId: string | null } | null>(null);

  // Andre bookinger per linje: prosjektnummer, eller tittel for interne.
  const othersByLine = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const c of conflicts) {
      const label = c.other_project_code ?? c.other_title ?? "intern booking";
      const list = map.get(c.booking_item_id) ?? [];
      if (!list.includes(label)) list.push(label);
      map.set(c.booking_item_id, list);
    }
    return map;
  }, [conflicts]);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">Booket utstyr</CardTitle>
        <div className="flex gap-2">
          {lines.length > 0 && (
            <Button size="sm" variant="outline" asChild>
              <Link to={`/prosjekter/${projectId}/pakkeliste`}>
                <SrIcon name="clipboard-list" className="h-4 w-4" />
                Pakkeliste
              </Link>
            </Button>
          )}
          <Button size="sm" onClick={() => setDialog({ bookingId: null })}>
            <SrIcon name="plus" className="h-4 w-4" />
            Book utstyr
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-destructive text-sm">Feil ved lasting av utstyr: {(error as Error).message}</p>
        ) : isLoading ? (
          <div className="flex justify-center py-6">
            <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen utstyr er booket på prosjektet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className={th}>Utstyr</th>
                <th className={th}>Når</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const others = othersByLine.get(l.id);
                return (
                  <tr
                    key={l.id}
                    onClick={() => setDialog({ bookingId: l.booking_id })}
                    className="border-b border-border/50 last:border-0 hover:bg-muted/30 cursor-pointer"
                  >
                    <td className="py-2.5 px-3">
                      <button
                        type="button"
                        className="font-medium hover:underline text-left"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialog({ bookingId: l.booking_id });
                        }}
                      >
                        {l.item_name}
                        {l.quantity > 1 ? ` × ${l.quantity}` : ""}
                      </button>
                      <span className="text-muted-foreground"> · {l.item_department_name ?? "Ukjent avdeling"}</span>
                      {others && <span className={`ml-2 font-medium ${TONE_TEXT.miss}`}>⚠ Også booket på {others.join(", ")}</span>}
                      {l.note && <div className="text-xs text-muted-foreground">{l.note}</div>}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap tabular-nums">{formatDayRange(l.date_from, l.date_to)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
      <BookingDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        bookingId={dialog?.bookingId ?? null}
        defaults={{ projectId }}
      />
    </Card>
  );
}
